import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Typography, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Props } from "./interfaces";

import { Container, MainListContainer, ExtensionListContainer, Title, List } from "./styles";
import { CarbonLocation, ClockinIcon, ClockoutIcon } from "../../../assets/icons";
import { QRCodeContainerIcon } from "../../../assets/icons";
import { TnAClockingDTO } from "../../../models/tna";
import { CustomTooltip } from "../../../reusable-components/CustomTooltip/CustomTooltip";
import SvgFromString from "../../../reusable-components/IconsSelect/SvgFromString/SvgFromString";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";
import { TNA_CLOCKING_TYPES } from "../../../utils/constants/tna-constants";
const { ClockIn } = TNA_CLOCKING_TYPES;

const ClockingsList = ({ title, list }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_clockings"; // tRP - translation root path
  const theme = useTheme();

  const firstForPrevDayClockingIndex = useMemo(() => {
    return list.findIndex((clocking) => clocking.forPrevDay);
  }, [list]);

  return (
    <Container>
      <Title>{title}</Title>

      {list.length ? (
        <MainListContainer>
          <List>
            {list
              .slice(0, 4)
              .map(
                (
                  {
                    id,
                    clockingType,
                    tnADetail,
                    isAuto,
                    isRealTimeClocking,
                    tnALocationCode,
                    tnALocationCodeOrName,
                    tnAClockingLocationQRCode,
                  },
                  idx
                ) => (
                  <Grid
                    key={id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {firstForPrevDayClockingIndex === idx && firstForPrevDayClockingIndex !== 0 && (
                      <Divider
                        orientation="horizontal"
                        sx={{ width: "100%" }}
                      />
                    )}

                    <Grid
                      key={id}
                      style={{
                        display: "flex",
                        alignItems: clockingType?.id === ClockIn.id ? "end" : "start",
                      }}
                    >
                      {tnALocationCode ? (
                        <CustomTooltip
                          title={`${tnALocationCode?.name} ${
                            tnAClockingLocationQRCode ? `(${t(`${tRP}.scanned_qr_code`)})` : ""
                          }`}
                          tooltipStyles={{ marginTop: "2px !important" }}
                        >
                          <span
                            style={{
                              width: 24,
                              height: 24,
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {tnAClockingLocationQRCode && (
                              <QRCodeContainerIcon
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: 24,
                                  height: 24,
                                  color: theme.palette.secondary.light,
                                }}
                              />
                            )}
                            <SvgFromString
                              svgString={tnALocationCode?.icon}
                              color={theme.palette.secondary.light}
                              width={18}
                              height={18}
                            />
                          </span>
                        </CustomTooltip>
                      ) : (
                        tnALocationCodeOrName && (
                          <CustomTooltip
                            title={tnALocationCodeOrName}
                            tooltipStyles={{ marginTop: "2px !important" }}
                          >
                            <span style={{ width: 20, height: 20 }}>
                              <CarbonLocation style={{ width: 20, height: 20, color: theme.palette.secondary.light }} />
                            </span>
                          </CustomTooltip>
                        )
                      )}
                      <Typography
                        key={id}
                        style={{ whiteSpace: "nowrap", alignItems: "center" }}
                      >
                        {TnAClockingDTO.ClockingString(tnADetail)}
                        <span
                          title={
                            isAuto
                              ? ""
                              : isRealTimeClocking
                              ? `${t(`${tRP}.manual_clocking`)} (${t(`${tRP}.real_time`)})`
                              : `${t(`${tRP}.manual_clocking`)} (${t(`${tRP}.not_real_time`)})`
                          }
                        >
                          {isAuto ? "" : isRealTimeClocking ? "" : "^"}
                        </span>
                      </Typography>
                      {clockingType?.id === ClockIn.id ? <ClockinIcon /> : <ClockoutIcon />}
                    </Grid>
                  </Grid>
                )
              )}
          </List>
          {list.length > 4 && (
            <SimplePopover
              buttonType="icon"
              buttonContent={<FontAwesomeIcon icon={faEllipsis} />}
            >
              <ExtensionListContainer>
                <List>
                  {list
                    .slice(4, list.length)
                    .map(({ id, clockingType, tnADetail, isAuto, isRealTimeClocking }, idx) => (
                      <Grid
                        key={id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {firstForPrevDayClockingIndex - 4 === idx && firstForPrevDayClockingIndex > 0 && (
                          <Divider
                            orientation="horizontal"
                            sx={{ width: "100%" }}
                          />
                        )}

                        <Grid
                          key={id}
                          style={{
                            display: "flex",
                            alignItems: clockingType?.id === ClockIn.id ? "end" : "start",
                          }}
                        >
                          <Typography
                            key={id}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {TnAClockingDTO.ClockingString(tnADetail)}
                            <span
                              title={
                                isAuto
                                  ? ""
                                  : isRealTimeClocking
                                  ? `${t(`${tRP}.manual_clocking`)} (${t(`${tRP}.real_time`)})`
                                  : `${t(`${tRP}.manual_clocking`)} (${t(`${tRP}.not_real_time`)})`
                              }
                            >
                              {isAuto ? "" : isRealTimeClocking ? "" : "^"}
                            </span>
                          </Typography>
                          {clockingType?.id === ClockIn.id ? <ClockinIcon /> : <ClockoutIcon />}
                        </Grid>
                      </Grid>
                    ))}
                </List>
              </ExtensionListContainer>
            </SimplePopover>
          )}
        </MainListContainer>
      ) : (
        <Typography>{t(`${tRP}.no_clockings`)}</Typography>
      )}
    </Container>
  );
};

export default ClockingsList;
